/**
 * Scheduled Booking Service
 * 
 * Handles logic for loading, converting, and managing scheduled bookings
 * Supports both demo mode (mock data) and live mode (API)
 */

import type { ScheduledBooking } from '@/types/scheduledBooking';
import type { TimesheetEntry } from '@/types/timesheet';
import { mockScheduledBookings } from '@/data/mockScheduledBookings';
import { apiClient } from './apiClient';
import { format } from 'date-fns';

let bookingEntryIdCounter = 0;

/**
 * API response type for scheduled bookings from backend
 */
interface ScheduledBookingApiResponse {
  id: number;
  employeeId: number;
  employeeName: string;
  projectId: string;
  projectName: string;
  projectCode: string;
  costCentreId: string;
  costCentreName: string;
  roleId?: number;
  roleName?: string;
  companyId: string;
  weekEnding: string;
  scheduledHours: {
    monday: number;
    tuesday: number;
    wednesday: number;
    thursday: number;
    friday: number;
    saturday: number;
    sunday: number;
  };
  createdBy: string;
  createdByName: string;
  createdAt: string;
  notes?: string;
  status: string;
}

/**
 * Convert API response to ScheduledBooking type
 */
function mapApiResponseToScheduledBooking(response: ScheduledBookingApiResponse): ScheduledBooking {
  return {
    id: response.id,
    employeeId: response.employeeId,
    employeeName: response.employeeName,
    projectId: response.projectId,
    projectName: response.projectName,
    projectCode: response.projectCode,
    costCentreId: response.costCentreId,
    costCentreName: response.costCentreName,
    roleId: response.roleId,
    roleName: response.roleName,
    companyId: response.companyId,
    weekEnding: new Date(response.weekEnding),
    scheduledHours: response.scheduledHours,
    createdBy: response.createdBy,
    createdByName: response.createdByName,
    createdAt: new Date(response.createdAt),
    notes: response.notes,
    status: response.status as 'active' | 'dismissed' | 'completed',
  };
}

/**
 * Get scheduled bookings for a specific employee and week from API (live mode)
 */
export async function getScheduledBookingsFromApi(
  employeeId: number,
  weekEnding: Date,
  companyId?: string
): Promise<ScheduledBooking[]> {
  try {
    const formattedDate = format(weekEnding, 'yyyy-MM-dd');
    const url = companyId
      ? `/scheduled-bookings/${employeeId}/${formattedDate}?companyId=${companyId}`
      : `/scheduled-bookings/${employeeId}/${formattedDate}`;
    const response = await apiClient.get<ScheduledBookingApiResponse[]>(url);
    return response.map(mapApiResponseToScheduledBooking);
  } catch (error) {
    console.error('Failed to fetch scheduled bookings from API:', error);
    return [];
  }
}

/**
 * Get scheduled bookings for a Resource Manager resource (contractor) and week from the
 * backend API. Contractor bookings key on the ResourceId rather than a platform user.
 */
export async function getScheduledBookingsForResourceFromApi(
  resourceId: number,
  weekEnding: Date,
  companyId?: string
): Promise<ScheduledBooking[]> {
  try {
    const formattedDate = format(weekEnding, 'yyyy-MM-dd');
    const url = companyId
      ? `/scheduled-bookings/resource/${resourceId}/${formattedDate}?companyId=${companyId}`
      : `/scheduled-bookings/resource/${resourceId}/${formattedDate}`;
    const response = await apiClient.get<ScheduledBookingApiResponse[]>(url);
    return response.map(mapApiResponseToScheduledBooking);
  } catch (error) {
    console.error('Failed to fetch resource scheduled bookings from API:', error);
    return [];
  }
}

/**
 * Get scheduled bookings for a specific employee and week (demo mode - mock data)
 */
export function getScheduledBookingsForWeek(
  employeeId: number,
  weekEnding: Date
): ScheduledBooking[] {
  return mockScheduledBookings.filter(
    booking =>
      booking.employeeId === employeeId &&
      booking.weekEnding.getTime() === weekEnding.getTime() &&
      booking.status === 'active'
  );
}

/**
 * Employee role information for defaulting the Role field on booking entries
 */
export interface EmployeeRoleInfo {
  roleId?: number;
  roleName?: string;
}

/**
 * Convert a scheduled booking to a timesheet entry
 * If employeeRole is provided, the entry's roleId/roleName will default to the employee's assigned role
 */
export function convertBookingToEntry(booking: ScheduledBooking, employeeRole?: EmployeeRoleInfo): TimesheetEntry {
  // Prefer role from booking (set by resource manager); fall back to employee's default role
  const roleId = booking.roleId ?? employeeRole?.roleId;
  const roleName = booking.roleId ? booking.roleName : (employeeRole?.roleId ? employeeRole.roleName : undefined);

  return {
    id: -Date.now() - ++bookingEntryIdCounter,
    projectId: booking.projectId,
    projectCode: booking.projectCode,
    projectName: booking.projectName,
    costCentreId: booking.costCentreId,
    costCentreName: booking.costCentreName,
    roleId,
    roleName,
    description: booking.notes || '',
    hours: { ...booking.scheduledHours },
    // Scheduling metadata
    isFromSchedule: true,
    scheduledBookingId: booking.id,
    scheduledHours: { ...booking.scheduledHours },
    isDismissed: false,
    bookingCreatedBy: booking.createdBy,
    bookingCreatedByName: booking.createdByName,
    bookingNotes: booking.notes,
    bookingCreatedAt: booking.createdAt,
  };
}

/**
 * Check if hours have been edited from scheduled
 */
export function hasEditedHours(entry: TimesheetEntry): boolean {
  if (!entry.isFromSchedule || !entry.scheduledHours) {
    return false;
  }

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  
  return days.some(day => entry.hours[day] !== entry.scheduledHours![day]);
}

/**
 * Check if an entry conflicts with a scheduled booking
 * (same project and cost centre for the same week)
 */
export function hasConflict(
  entry: TimesheetEntry,
  scheduledBookings: ScheduledBooking[]
): ScheduledBooking | null {
  if (entry.isFromSchedule) {
    return null; // Scheduled entries don't conflict with themselves
  }

  const conflict = scheduledBookings.find(
    booking =>
      booking.projectId === entry.projectId &&
      booking.costCentreId === entry.costCentreId
  );

  return conflict || null;
}

/**
 * Dismiss a scheduled booking
 */
export function dismissBooking(
  entry: TimesheetEntry,
  reason: string
): TimesheetEntry {
  return {
    ...entry,
    isDismissed: true,
    dismissedAt: new Date(),
    dismissalReason: reason,
    // Clear hours when dismissed
    hours: {
      monday: 0,
      tuesday: 0,
      wednesday: 0,
      thursday: 0,
      friday: 0,
      saturday: 0,
      sunday: 0,
    },
  };
}

/**
 * Restore a dismissed booking
 */
export function restoreBooking(entry: TimesheetEntry): TimesheetEntry {
  if (!entry.scheduledHours) {
    return entry;
  }

  return {
    ...entry,
    isDismissed: false,
    dismissedAt: undefined,
    dismissalReason: undefined,
    // Restore original scheduled hours
    hours: { ...entry.scheduledHours },
  };
}

/**
 * Auto-load scheduled bookings for a timesheet (demo mode - synchronous)
 * This merges scheduled bookings with existing entries
 */
export function autoLoadScheduledBookings(
  employeeId: number,
  weekEnding: Date,
  existingEntries: TimesheetEntry[],
  employeeRole?: EmployeeRoleInfo
): TimesheetEntry[] {
  const scheduledBookings = getScheduledBookingsForWeek(employeeId, weekEnding);
  
  // Convert bookings to entries, defaulting the Role from the employee's assigned role
  const scheduledEntries = scheduledBookings.map(b => convertBookingToEntry(b, employeeRole));
  
  // Filter out scheduled entries that already exist (by booking ID)
  const existingBookingIds = new Set(
    existingEntries
      .filter(e => e.scheduledBookingId)
      .map(e => e.scheduledBookingId)
  );
  
  const newScheduledEntries = scheduledEntries.filter(
    entry => !existingBookingIds.has(entry.scheduledBookingId)
  );
  
  // Combine existing entries with new scheduled entries
  // Scheduled entries go first
  return [...newScheduledEntries, ...existingEntries];
}

/**
 * Auto-load scheduled bookings for a timesheet (live mode - async API call)
 * This merges scheduled bookings from the API with existing entries
 */
export async function autoLoadScheduledBookingsAsync(
  employeeId: number,
  weekEnding: Date,
  existingEntries: TimesheetEntry[],
  employeeRole?: EmployeeRoleInfo,
  companyId?: string
): Promise<TimesheetEntry[]> {
  const scheduledBookings = await getScheduledBookingsFromApi(employeeId, weekEnding, companyId);
  
  // Convert bookings to entries, defaulting the Role from the employee's assigned role
  const scheduledEntries = scheduledBookings.map(b => convertBookingToEntry(b, employeeRole));
  
  // Filter out scheduled entries that already exist (by booking ID)
  const existingBookingIds = new Set(
    existingEntries
      .filter(e => e.scheduledBookingId)
      .map(e => e.scheduledBookingId)
  );
  
  const newScheduledEntries = scheduledEntries.filter(
    entry => !existingBookingIds.has(entry.scheduledBookingId)
  );
  
  // Combine existing entries with new scheduled entries
  // Scheduled entries go first
  return [...newScheduledEntries, ...existingEntries];
}

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;

/**
 * Check if an entry's hours have any non-zero values
 */
function hasAnyHours(hours: TimesheetEntry['hours']): boolean {
  return DAYS.some(day => hours[day] !== 0);
}

/**
 * Synchronise existing timesheet entries with the latest scheduled bookings.
 *
 * For editable timesheets (draft / rejected / recalled) this performs a 3-way
 * reconciliation:
 *   • UPDATE – booking still exists → refresh metadata & scheduled hours.
 *             If the user has NOT edited actual hours, update those too.
 *   • REMOVE – booking deleted → remove the entry, unless the user edited
 *             hours, in which case convert it to a manual entry.
 *   • ADD    – new booking with no matching entry → create a new entry.
 *
 * Dismissed entries are left untouched.
 */
export function syncScheduledBookings(
  latestBookings: ScheduledBooking[],
  existingEntries: TimesheetEntry[],
  employeeRole?: EmployeeRoleInfo
): TimesheetEntry[] {
  const bookingMap = new Map(latestBookings.map(b => [b.id, b]));

  const reconciledEntries: TimesheetEntry[] = [];
  const matchedBookingIds = new Set<number>();

  for (const entry of existingEntries) {
    // Non-schedule entries pass through unchanged
    if (!entry.isFromSchedule || !entry.scheduledBookingId) {
      reconciledEntries.push(entry);
      continue;
    }

    // Dismissed entries are left untouched
    if (entry.isDismissed) {
      matchedBookingIds.add(entry.scheduledBookingId);
      reconciledEntries.push(entry);
      continue;
    }

    const booking = bookingMap.get(entry.scheduledBookingId);

    if (booking) {
      // UPDATE – booking still exists
      matchedBookingIds.add(booking.id);
      const userEdited = hasEditedHours(entry);

      // Prefer role from booking; fall back to employee's default role
      const updatedRoleId = booking.roleId ?? employeeRole?.roleId;
      const updatedRoleName = booking.roleId ? booking.roleName : (employeeRole?.roleId ? employeeRole.roleName : undefined);

      reconciledEntries.push({
        ...entry,
        // Refresh project / cost centre / role / metadata from latest booking
        projectId: booking.projectId,
        projectCode: booking.projectCode,
        projectName: booking.projectName,
        costCentreId: booking.costCentreId,
        costCentreName: booking.costCentreName,
        roleId: updatedRoleId,
        roleName: updatedRoleName,
        bookingCreatedBy: booking.createdBy,
        bookingCreatedByName: booking.createdByName,
        bookingNotes: booking.notes,
        bookingCreatedAt: booking.createdAt,
        description: userEdited ? entry.description : (booking.notes || ''),
        // Always update the scheduled hours baseline
        scheduledHours: { ...booking.scheduledHours },
        // Only update actual hours if user has not edited them
        hours: userEdited ? entry.hours : { ...booking.scheduledHours },
      });
    } else {
      // REMOVE – booking was deleted
      if (hasEditedHours(entry) || hasAnyHours(entry.hours)) {
        // User has work recorded – convert to manual entry
        reconciledEntries.push({
          ...entry,
          isFromSchedule: false,
          scheduledBookingId: undefined,
          scheduledHours: undefined,
          bookingCreatedBy: undefined,
          bookingCreatedByName: undefined,
          bookingNotes: undefined,
          bookingCreatedAt: undefined,
        });
      }
      // else: no user edits and no hours → silently drop the entry
    }
  }

  // ADD – new bookings that have no matching entry
  for (const booking of latestBookings) {
    if (!matchedBookingIds.has(booking.id)) {
      reconciledEntries.push(convertBookingToEntry(booking, employeeRole));
    }
  }

  return reconciledEntries;
}

/**
 * Synchronise scheduled bookings for a timesheet (demo mode – synchronous).
 * Fetches latest bookings from mock data and reconciles with existing entries.
 */
export function syncScheduledBookingsForWeek(
  employeeId: number,
  weekEnding: Date,
  existingEntries: TimesheetEntry[],
  employeeRole?: EmployeeRoleInfo
): TimesheetEntry[] {
  const latestBookings = getScheduledBookingsForWeek(employeeId, weekEnding);
  return syncScheduledBookings(latestBookings, existingEntries, employeeRole);
}

/**
 * Synchronise scheduled bookings for a timesheet (live mode – async API call).
 * Fetches latest bookings from the API and reconciles with existing entries.
 */
export async function syncScheduledBookingsAsync(
  employeeId: number,
  weekEnding: Date,
  existingEntries: TimesheetEntry[],
  employeeRole?: EmployeeRoleInfo,
  companyId?: string
): Promise<TimesheetEntry[]> {
  const latestBookings = await getScheduledBookingsFromApi(employeeId, weekEnding, companyId);
  return syncScheduledBookings(latestBookings, existingEntries, employeeRole);
}

/**
 * Calculate total scheduled hours for an entry
 */
export function getTotalScheduledHours(entry: TimesheetEntry): number {
  if (!entry.scheduledHours) {
    return 0;
  }

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  return days.reduce((sum, day) => sum + entry.scheduledHours![day], 0);
}

/**
 * Calculate total actual hours for an entry
 */
export function getTotalActualHours(entry: TimesheetEntry): number {
  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
  return days.reduce((sum, day) => sum + entry.hours[day], 0);
}
